import { ToastType } from '@components/toast';
import { SubjectivityUpdateRequestBody } from '@lio/api-types';
import { subjectivityQueryKeys } from '@services/subjectivity/keys';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useSnackbar } from 'notistack';

export const useUpdateSubjectivity = (quoteId?: string) => {
  const queryClient = useQueryClient();
  const client = useClient();
  const { enqueueSnackbar } = useSnackbar();
  const mutationFn = ({
    label,
    status,
    dueDaysAfterPolicyEffective,
    description,
    subjectivityId,
    formData,
    insuranceLineId,
    bypassReason,
    bypassed,
    suppressSubjectivityCompletePolicyGeneratedEmail,
  }: Omit<SubjectivityUpdateRequestBody, 'bypassReason'> & {
    subjectivityId: string;
    insuranceLineId?: string | null;
    bypassReason?: string;
  }) =>
    client.patch(`subjectivity/${subjectivityId}`, {
      json: {
        label,
        status,
        dueDaysAfterPolicyEffective,
        description,
        formData,
        insuranceLineId,
        bypassReason,
        bypassed,
        suppressSubjectivityCompletePolicyGeneratedEmail,
      },
    });
  const result = useMutation({
    mutationFn,
    onSuccess: () => {
      if (quoteId) {
        queryClient.invalidateQueries({ queryKey: subjectivityQueryKeys.search(quoteId) });
      } else {
        // fallback invalidate all queries with 'subjectivities' in the key if quoteid is undefined
        queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes('subjectivities') });
      }
      enqueueSnackbar(`Subjectivity updated`, {
        preventDuplicate: true,
        variant: ToastType.Success,
      });
    },
    onError: async (e) => {
      const message = await getApiErrorMessage(e, 'Subjectivity update failed');
      enqueueSnackbar(message, {
        preventDuplicate: true,
        variant: ToastType.Error,
        persist: true,
      });
    },
  });
  return { ...result, updateSubjectivity: result.mutateAsync };
};
